/* /assets/wim-related-products.js */
(function () {
  'use strict';

  const API_TEMPLATE = '[data-api-related-template]';
  const PRODUCT_ITEM = '.product-item';
  const GRID_ITEM = '[data-grid-item]';
  const RELATED_SECTION = '[data-related-section]';
  const RECENTLY_VIEWED = 'recently-viewed';
  const GRID_SLIDER = 'grid-slider';
  const RECENTLY_VIEWED_WRAPPER = '[data-recently-viewed-wrapper]';
  const FALLBACK_CONTAINER = '[data-related-fallback]';
  const ATTR_LIMIT = 'data-limit';
  const ATTR_PRODUCT_ID = 'data-product-id';
  const ATTR_LAYOUT = 'data-layout';
  const ATTR_FALLBACK = 'data-fallback';
  const ATTR_VIEW_BRAND = 'data-view-brand';
  const CLASS_HIDDEN = 'hidden';
  const CLASS_MOBILE_SLIDER = 'grid--mobile-slider';
  const CLASS_SLIDER = 'grid--slider';

  if (customElements.get('wim-related-products')) return;

  customElements.define(
    'wim-related-products',
    class extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.id;
        this.relatedItems = 0;
        this.wrapper = this.querySelector(RECENTLY_VIEWED_WRAPPER);
        this.recentlyViewed = this.querySelector(RECENTLY_VIEWED);
      }

      connectedCallback() {
        this.loadRelatedProducts();
        this.loadRecentlyViewedProducts();
      }

      // Build the recommendations URL for a given intent, carrying brand context
      // because the Section Rendering API request has no template suffix / view_brand,
      // so brand-mode.liquid would otherwise resolve to 'main'.
      buildUrl(section, intent) {
        const productId = section.getAttribute(ATTR_PRODUCT_ID);
        const limit = section.getAttribute(ATTR_LIMIT);
        const viewBrand = (section.getAttribute(ATTR_VIEW_BRAND) || '').trim();

        let url =
          `${window.theme.routes.product_recommendations_url}` +
          `?section_id=api-product-recommendation&limit=${limit}` +
          `&product_id=${productId}&intent=${intent}`;

        if (viewBrand) url += `&view_brand=${encodeURIComponent(viewBrand)}`;

        return url;
      }

      loadRelatedProducts() {
        const section = this.querySelector(RELATED_SECTION);
        if (!section) return;

        const url = this.buildUrl(section, 'related');

        fetch(url)
          .then((response) => response.text())
          .then((html) => this.handleRelatedProductsResponse(html, section))
          .catch(() => this.handleEmptyRelated(section));
      }

      // Parse the API template fragment and count rendered grid items.
      countItems(html) {
        const holder = document.createElement('div');
        holder.innerHTML = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector(API_TEMPLATE).innerHTML;
        return { holder, count: holder.querySelectorAll(GRID_ITEM).length };
      }

      renderItems(section, holder) {
        section.innerHTML = holder.innerHTML;
        const mobileColumns = parseInt(section.style.getPropertyValue('--COLUMNS-MOBILE'));
        const firstItem = section.querySelector(GRID_ITEM);
        if (!firstItem) return;
        if (mobileColumns === 0) firstItem.parentElement.classList.add(CLASS_MOBILE_SLIDER);
        if (section.getAttribute(ATTR_LAYOUT) === 'slider') {
          firstItem.parentElement.classList.add(CLASS_SLIDER);
        } else {
          firstItem.parentElement.classList.remove(CLASS_SLIDER);
        }

        // The injected markup includes a fresh <grid-slider>; trigger its init so
        // it measures widths now that the items are in the (visible) layout.
        const slider = section.querySelector(GRID_SLIDER);
        if (slider) {
          slider.dispatchEvent(new CustomEvent('theme:grid-slider:init', { bubbles: true }));
        }
      }

      handleRelatedProductsResponse(html, section) {
        const { holder, count } = this.countItems(html);

        if (count > 0) {
          this.renderItems(section, holder);
          this.relatedItems = count;
          this.updateVisibility();
        } else {
          this.handleEmptyRelated(section);
        }
      }

      // Decide what to do when the related intent returns no products.
      handleEmptyRelated(section) {
        const fallback = section.getAttribute(ATTR_FALLBACK) || 'hide';

        if (fallback === 'complementary') {
          this.loadComplementaryProducts(section);
        } else if (fallback === 'first_collection' || fallback === 'pick_collection') {
          this.revealCollectionFallback(section);
        } else {
          this.hideSection(section);
        }
      }

      loadComplementaryProducts(section) {
        const url = this.buildUrl(section, 'complementary');

        fetch(url)
          .then((response) => response.text())
          .then((html) => {
            const { holder, count } = this.countItems(html);
            if (count > 0) {
              this.renderItems(section, holder);
              this.relatedItems = count;
              this.updateVisibility();
            } else {
              this.revealCollectionFallback(section);
            }
          })
          .catch(() => this.revealCollectionFallback(section));
      }

      // Reveal the pre-rendered (server-side) collection container if present;
      // otherwise hide the section. Used as the complementary fallback's last
      // resort too, so a misconfigured complementary still degrades gracefully.
      revealCollectionFallback(section) {
        const tabContent = section.closest('.tab-content');
        const container = tabContent && tabContent.querySelector(FALLBACK_CONTAINER);

        if (container && container.querySelectorAll(GRID_ITEM).length > 0) {
          container.hidden = false;
          this.relatedItems = container.querySelectorAll(GRID_ITEM).length;
          const slider = container.querySelector(GRID_SLIDER);
          if (slider) {
            slider.dispatchEvent(new CustomEvent('theme:grid-slider:init', { bubbles: true }));
          }
          this.updateVisibility();
        } else {
          this.hideSection(section);
        }
      }

      loadRecentlyViewedProducts() {
        if (!this.recentlyViewed) return;
        this.recentlyViewed.addEventListener('theme:recently-viewed:loaded', () => {
          this.handleRecentlyViewedResponse();
        });
      }

      handleRecentlyViewedResponse() {
        const minimum = parseInt(this.recentlyViewed.dataset.minimum) || 1;
        const items = this.recentlyViewed.querySelectorAll(PRODUCT_ITEM);
        const slider = this.recentlyViewed.querySelector(GRID_SLIDER);
        const showWithoutWrapper = !this.wrapper && items.length > 0;
        const showWithWrapper = this.wrapper && items.length >= minimum;

        if (showWithoutWrapper || showWithWrapper) {
          if (showWithWrapper) this.wrapper.classList.remove(CLASS_HIDDEN);
          this.recentlyViewed.classList.remove(CLASS_HIDDEN);
          this.recentlyViewed.dispatchEvent(new CustomEvent('theme:tab:check', { bubbles: true }));
          if (slider) slider.dispatchEvent(new CustomEvent('theme:grid-slider:init', { bubbles: true }));
        }

        this.updateVisibility();
      }

      hideSection(section) {
        section.dispatchEvent(new CustomEvent('theme:tab:hide', { bubbles: true }));
      }

      updateVisibility() {
        const isEmpty = Shopify.Products.getConfig().howManyToShow < 1 && this.relatedItems < 1;
        this.classList.toggle(CLASS_HIDDEN, isEmpty);
      }
    }
  );
})();
